'use client'

/**
 * MapSection — Phase 3 section kind `map`.
 *
 * Variant B (Round 1 pick): contained 16:9 rounded card with drop shadow,
 * static map image → click → interactive Google Maps iframe.
 *
 *   - First paint: zero JS for the map itself — a Google Static Maps
 *     `<img>` of the dealer's location. The whole image is the click
 *     target (no overlay chrome); it reads as the map, not a "tap to
 *     load" placeholder. Static Maps geocodes the plain address string
 *     server-side, so no lat/lng column on `tenants.address` is needed.
 *   - On click / Enter: the static <img> swaps to a Google Maps
 *     `?output=embed` iframe (no API key required). Interactive zoom +
 *     pan + directions.
 *   - No API key (env unset) → renders the interactive iframe directly
 *     (no static-image stage), so there's never a blank/affordance-less
 *     box. Keeps the section usable in local dev and any environment
 *     where the key hasn't been provisioned.
 *
 * `data` is display config plus ONE piece of selection state: `location_id`.
 * Which `tenant_locations` row that resolves to is decided upstream (the
 * caller owns the location list) and arrives as the `location` prop —
 * `data.location_id` when set, else the dealer's primary location. Respects
 * that location's `show_public` so dealers can hide the pill without removing
 * the section. The section self-hides until it has at least a street address
 * (without one the marker can't be placed), which is also how an
 * explicit-but-unresolvable `location_id` lands: nothing renders, rather than
 * a confident pin on the wrong location.
 *
 * `apiKey` is the Maps Static API key, threaded from the storefront
 * (`process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY`) rather than read here —
 * shared-ui stays env-agnostic so the same component builds under both
 * Vite (portal) and Next (storefront).
 *
 * The 'use client' boundary is the minimum surface — the static <img> is
 * inlined into the component, but the swap-to-iframe needs `useState`.
 * The surrounding section wrapper stays server-rendered upstream.
 */

import { useState } from 'react'
import type { Tenant, TenantLocation } from '@sifly/shared-types'

export type MapSectionData = {
  aspect?: '16x9' | '21x9' | '4x5' | '1x1'
  show_address_pill?: boolean
  zoom?: number
  /** See the schema in agent-foundation: which location to map. Resolution
   *  happens upstream (the caller owns the tenant's location list); by the time
   *  it reaches this component the choice is already made. */
  location_id?: string
}

export type MapSectionProps = {
  data: MapSectionData
  tenant: Pick<Tenant, 'name'>
  /** The location to map, resolved at the storefront's section boundary:
   *  `data.location_id` when set, else the dealer's primary location. Supplies
   *  the marker address (street/city/state/zip) and gates the address pill on
   *  its `show_public`. Null → the section self-hides, which is also how an
   *  explicit-but-unresolvable `location_id` arrives — never a wrong address. */
  location?: TenantLocation | null
  /** Label for the address pill. The tenant name for the primary/default case;
   *  the location's own name once a page has picked a specific place, since
   *  "Adventure Elevated" over a Jordanelle pin says less than "Jordanelle". */
  pillTitle?: string
  /** Maps Static API key. When omitted, first paint degrades to the shell. */
  apiKey?: string
}

const ASPECT_CLASSES: Record<NonNullable<MapSectionData['aspect']>, string> = {
  '16x9': 'aspect-[16/9]',
  '21x9': 'aspect-[21/9]',
  '4x5': 'aspect-[4/5]',
  '1x1': 'aspect-square',
}

// Static Maps caps each dimension at 640px (1280 with scale=2). One size
// per aspect keeps the image proportional to the card; scale=2 covers
// retina.
const STATIC_SIZE: Record<NonNullable<MapSectionData['aspect']>, [number, number]> = {
  '16x9': [640, 360],
  '21x9': [640, 274],
  '4x5': [512, 640],
  '1x1': [640, 640],
}

function staticMapUrl(
  addrQuery: string,
  zoom: number,
  aspect: NonNullable<MapSectionData['aspect']>,
  apiKey: string,
): string {
  const [w, h] = STATIC_SIZE[aspect]
  const params = new URLSearchParams({
    center: addrQuery,
    zoom: String(zoom),
    size: `${w}x${h}`,
    scale: '2',
    maptype: 'roadmap',
    // A default red marker at the same geocoded address — universally read
    // as "the location" without needing a brand-color legend.
    markers: addrQuery,
    key: apiKey,
  })
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`
}

export function MapSection({ data, tenant, location, pillTitle, apiKey }: MapSectionProps) {
  const [interactive, setInteractive] = useState(false)

  const address = location?.address
  const showPill = data.show_address_pill !== false
  const showAddress = !!location?.show_public
  const title = pillTitle || tenant.name
  const aspect = data.aspect ?? '16x9'
  const zoom = data.zoom ?? 14
  const aspectClass = ASPECT_CLASSES[aspect]

  const addrParts = [
    address?.street,
    address?.city,
    address?.state,
    address?.zip,
  ].filter(Boolean) as string[]
  const addrQuery = addrParts.join(', ')
  // Require a street before we render anything — without it neither the
  // static map nor the iframe can place a marker, and a partial address
  // geocodes to the wrong place. Section self-hides until the dealer fills
  // in at least a street address.
  if (!address?.street) return null

  const iframeUrl = `https://maps.google.com/maps?q=${encodeURIComponent(addrQuery)}&output=embed`
  // Static-map first paint only when we have a key; otherwise the shell
  // keeps the click-to-load affordance without a map image.
  const staticUrl = apiKey ? staticMapUrl(addrQuery, zoom, aspect, apiKey) : null

  return (
    <section
      className="max-w-[var(--section-max-width,var(--max-width-page))] mx-auto px-[var(--section-padding-x,1rem)] py-[var(--space-section)]"
      data-component="MapSection"
    >
      <div
        className={`relative w-full ${aspectClass} rounded-2xl overflow-hidden shadow-[var(--shadow-card-elevated)]`}
      >
        {interactive || !staticUrl ? (
          <iframe
            title={`Map: ${title}`}
            src={iframeUrl}
            className="absolute inset-0 w-full h-full border-0"
            loading="lazy"
            allowFullScreen
          />
        ) : (
          // First paint: the static map image is the entire click target —
          // the map swaps to the interactive iframe on click. No overlay
          // chrome; it should read as the map, not a placeholder. (No key →
          // the iframe renders directly, above, so we never show this.)
          <button
            type="button"
            onClick={() => setInteractive(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setInteractive(true)
              }
            }}
            aria-label={`Open interactive map for ${title}`}
            className="absolute inset-0 w-full h-full cursor-pointer"
          >
            <img
              src={staticUrl}
              alt={`Map of ${title}`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </button>
        )}
        {showPill && showAddress && (
          <div className="pointer-events-none absolute bottom-4 left-4 bg-white px-4 py-3 rounded-lg shadow-[var(--shadow-card-soft)] max-w-[280px]">
            <div className="font-semibold text-[var(--color-text-primary)]">{title}</div>
            {address?.street && (
              <div className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                {address.street}
                {address.city && <br />}
                {[address.city, address.state, address.zip]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
